import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subject, Subscription } from "rxjs";

import { AuthService } from "src/app/core/core-services/services/auth.service";
import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { DialogService } from "src/app/core/core-services/services/dialog.service";
import { NotificationService } from "src/app/core/core-services/services/notification.service";
import { SharedDataService } from "src/app/core/core-services/services/shared-data.service";
import { ShortcutService } from "src/app/core/core-services/services/shortcut.service";

import { ApplicationConstants } from "src/app/core/core-services/constants/application.constants";
import { getPlanId, PlansConstants } from "src/app/core/core-services/constants/plans.constants";
import { TinyMceConfigService } from "src/app/core/core-services/services/tiny-mce-config.service";
import { Store } from "@ngrx/store";
import { selectPlanFeatures } from "src/app/core/store/plan/plan.selectors";
import { PlanFeatures } from "src/app/core/models/stripe.models";
import { WarningDialogComponent } from "src/app/core/core-services/warning-dialog/warning-dialog.component";

import { ScrollService } from "src/app/core/core-services/services/scroll.service";
import {
  FLYCUT_OUTSIDE_PLAN_ALLOCATED,
  FLYSHARE_ALL_ROUTE,
  FLYSHARE_OTHERS_ROUTE,
  FLYSHARE_SHARED_ROUTE,
} from "src/app/vengreso/share/flyshare/flyshare.config";
import { VersionHistoryComponent } from "../../shortcuts/version-history/version-history.component";
import { AddToFlycutsComponent } from "../add-to-flycuts/add-to-flycuts.component";
import { FeatureNotInPlanComponent } from "../feature-not-in-plan/feature-not-in-plan.component";
import { FlyCutShareComponent } from "../fly-cut-share/fly-cut-share.component";
import { ShareHistoryComponent } from "../share-history/share-history.component";

@Component({
    selector: "app-flyshare",
    templateUrl: "./flyshare.component.html",
    styleUrls: ["./flyshare.component.scss"],
    standalone: false
})
export class FlyshareComponent implements OnInit {
  shortcutIds: string[];
  activeRoute: string;
  shortcuts: any[] = [];
  userId: any;
  upScroll: boolean = true;

  allShortCutChecked: boolean = false;
  noShortcutChecked: boolean = false;
  panelOpenState = {};
  panelExpandState = {};
  currentIndex: number;
  subscriptions = new Subscription();
  modelChanged = new Subject<string>();
  editorManager: any;

  editorConfig: any;
  isSaving: boolean;
  editorApiKey: string;
  testingRichEditor: string;

  // Current plan features and ID for validation
  currentFeatures: PlanFeatures;
  currentPlanId: string;

  constructor(
    private route: ActivatedRoute,
    private sharedData: SharedDataService,
    private dataService: DataService,
    private authService: AuthService,
    private notification: NotificationService,
    public dialog: DialogService,
    // private loadingService: LoadingService,
    private shortcutService: ShortcutService,
    public scrollService: ScrollService,
    private cdr: ChangeDetectorRef,
    private tinyMceConfigService: TinyMceConfigService,
    private store: Store
  ) {
    this.userId = this.authService.userDetails.user_details.id;
  }

  async ngOnInit() {
    this.editorApiKey = ApplicationConstants.TINYMCE_API_KEY;
    this.currentPlanId = getPlanId();

    this.subscriptions.add(
      this.store.select(selectPlanFeatures).subscribe((features: PlanFeatures) => {
        if (features) {
          this.currentFeatures = features;
          this.updateEditorConfig();
        }
      })
    );

    this.route.params.subscribe(({ id }) => {
      this.init(id);
    });
  }

  private updateEditorConfig() {
    const baseConfig = this.shortcutService.getEditorConfig();
    this.editorConfig = this.tinyMceConfigService.getPlanBasedConfig(
      this.currentFeatures,
      baseConfig,
      this,
      this.currentPlanId,
      (type, error, showTheStarter) => {
        this.dialog.openDialogComponent(
          WarningDialogComponent,
          { type, error, showTheStarter },
          "500px"
        );
      }
    );
    this.editorConfig.placeholder = this.testingRichEditor;
    this.cdr.detectChanges();
  }

  init(id: string) {
    // this.loadingService.start();

    this.activeRoute = id;

    switch (id) {
      case FLYSHARE_OTHERS_ROUTE:
        this.getSharedWithOthersFlyShareShortcuts();
        break;

      case FLYSHARE_SHARED_ROUTE:
        this.getShortcutsOthersShared();
        break;

      case FLYSHARE_ALL_ROUTE:
      default:
        this.getAllUserFlyShareShortcuts();
        this.shortcutIds = this.sharedData.getAttribute("shortcutIds") ?? [];
        break;
    }

    if (this.shortcuts) {
      this.upScroll = false;
    }
  }

  openShareHistory(clonedShortcut: any, _e: Event) {
    if (_e) {
      _e.stopPropagation();
    }

    this.dialog.openDialogComponent(ShareHistoryComponent, { clonedShortcut }, "420px");
  }

  openedPanel(id) {
    this.panelOpenState[id] = true;
  }

  share() {
    const checked = this.shortcuts.filter((shortcut) => shortcut.checked === true);

    if (!checked || checked.length < 1) {
      this.notification.toast("You should select at least one FlyCut to share.");

      this.noShortcutChecked = true;

      setTimeout(() => {
        this.noShortcutChecked = false;
      }, 3000);

      return;
    }

    const fromFlyPlates = checked.some(({ user_defined }) => user_defined === false);

    this.dialog.openDialogComponent(
      FlyCutShareComponent,
      { shortcuts: checked, fromFlyPlates },
      fromFlyPlates ? "407px" : "859px"
    );
  }

  addToFlycuts(shortcut: any) {
    if (!this.isShortcutInUserPlan(shortcut)) {
      const dialogHandle = this.dialog.openDialogComponent(
        FeatureNotInPlanComponent,
        {
          added: this.getNumberOfFlycutsOutsidePlanAdded(),
          total: FLYCUT_OUTSIDE_PLAN_ALLOCATED,
        },
        "550px"
      );

      dialogHandle.afterClosed().subscribe((data: string) => {
        if (data !== "add") {
          return;
        }

        this.dialog.openDialogComponent(AddToFlycutsComponent, { self: this, shortcut }, "400px");
      });

      return;
    }

    this.dialog.openDialogComponent(AddToFlycutsComponent, { self: this, shortcut }, "400px");
  }

  resetInMyFlyCuts(shortcut: any) {
    this.dialog
      .confirm({
        title: "Just to Confirm...",
        message:
          "Resetting this message will remove any changes that you've made. You can always go back to previous versions through the version history.",
        confirmLabel: "Reset",
        showClose: true,
      })
      .subscribe((confirmed) => confirmed && this.resetShare(shortcut));
  }

  private resetShare(shortcut: any) {
    this.dataService.put({ url: `v1/shares-shortcut/${shortcut.id}` }).subscribe({
      next: () => {
        this.notification.toast("FlyShare reset successfully.");
        this.init(this.activeRoute);
      },
      error: (error) => {
        this.notification.toast(error.error.error ?? "Something went wrong. Contact administrator");
      },
    });
  }

  deleteShortcut(shortcut: any) {
    this.dialog
      .confirm({
        title: "Do you want to <strong>delete</strong> this?",
        message: "Deleted items cannot be restored.",
        variant: "danger",
        confirmLabel: "Delete",
      })
      .subscribe((confirmed) => confirmed && this.delete(shortcut));
  }

  delete(shortcut: any) {
    const url = `v1/${shortcut.shared_with_others === true ? "cloned-shared-shortcut" : "shares-shortcut"}/${shortcut.id
      }`;
    this.dataService.delete({ url: url }).subscribe({
      next: (response: any) => {
        this.init(this.activeRoute);
        this.notification.toast("FlyShare deleted successfully!");
      },
      error: (error) => {
        this.notification.toast(error.error.error ?? "Something went wrong, Contact Administrator", "Ok", 2000);
      },
    });
  }

  getAllUserFlyShareShortcuts() {
    this.dataService.get({ url: `v1/user/flyshares/shortcuts`, isLoader: false }).subscribe({
      next: (response: any) => {
        this.shortcuts = response?.result;

        if (this.shortcutIds && this.shortcutIds.length > 0 && this.shortcuts.length > 0) {
          let shortcutToExpand = this.shortcuts.find(
            (shortcut) => shortcut.reference_cloned_shortcut_id === this.shortcutIds[0]
          );
          this.panelExpandState[shortcutToExpand.id] = true;
          //this.scrollService.scrollTo(document.getElementById(shortcutToExpand.id))
          this.sharedData.deleteAttribute("shortcutIds");
        }
      },
      error: (error) => {
        this.notification.toast("Something went wrong, Contact Administrator", "Ok", 2000);
      },
      complete: () => {
        // this.loadingService.stop();
      },
    });
  }

  getSharedWithOthersFlyShareShortcuts() {
    this.dataService
      .get({
        url: `v1/user/flyshares/shortcuts-shared-with-others`,
        isLoader: false,
      })
      .subscribe({
        next: (response: any) => {
          this.shortcuts = response?.result;
        },
        error: (error) => {
          this.notification.toast("Something went wrong, Contact Administrator", "Ok", 2000);
        },
        complete: () => {
          // this.loadingService.stop();
        },
      });
  }

  getShortcutsOthersShared() {
    this.dataService
      .get({
        url: `v1/user/flyshares/shortcuts-others-shared`,
        isLoader: false,
      })
      .subscribe({
        next: (response: any) => {
          this.shortcuts = response?.result;
        },
        error: (error) => {
          this.notification.toast("Something went wrong, Contact Administrator", "Ok", 2000);
        },
        complete: () => {
          // this.loadingService.stop();
        },
      });
  }

  updateCheckList(isChecked: boolean, index: number) {
    if (index === -1) {
      this.shortcuts = this.shortcuts.map((item) => {
        item.checked = isChecked;
        return item;
      });
      return;
    }

    this.allShortCutChecked = true;
    for (const item of this.shortcuts) {
      if (!item.checked) {
        this.allShortCutChecked = false;
        break;
      }
    }
  }

  openVersionHistory(shortcut, expanded) {
    const id = shortcut.reference_shortcut_id;

    this.dialog.openDialogComponent(VersionHistoryComponent, { self: this, id }, "800px");

    if (!expanded) {
      this.panelOpenState[shortcut.id] = {};
      this.panelExpandState[shortcut.id] = {};
    }
  }

  onChangeContent(index: number) {
    this.modelChanged.next(null);
    this.currentIndex = index;
  }

  setEditorManager(event: any) {
    this.editorManager = event.editor.editorManager;
  }

  viewAll(): boolean {
    return this.activeRoute === FLYSHARE_ALL_ROUTE;
  }

  viewShared(): boolean {
    return this.activeRoute === FLYSHARE_SHARED_ROUTE;
  }

  viewOthers(): boolean {
    return this.activeRoute === FLYSHARE_OTHERS_ROUTE;
  }

  getNumberOfFlycutsOutsidePlanAdded(): number {
    return this.shortcuts.filter(
      (shortcut: any) =>
        !shortcut.shared_with_others && shortcut.added_to_flycuts && !this.isShortcutInUserPlan(shortcut)
    ).length;
  }

  isShortcutInUserPlan(shortcut: any): boolean {
    if (!this.currentFeatures || !this.currentPlanId) {
      return true; // Default to true if features not loaded yet to avoid flickering blocks
    }

    const plainText = this.shortcutService.getInlineStyledHTML(shortcut.html)
      .replace(/&nbsp;/g, " ")
      .replace(/<\/?[^>]+(>|$)/g, "");

    const result = this.tinyMceConfigService.validateShortcutContent(
      shortcut.html,
      plainText,
      this.currentFeatures,
      this.currentPlanId
    );

    this.cdr.detectChanges();
    return result;
  }
}
